import { environment } from '@env/environment';

// Define the properties we want to redact
const redactedProperties = ['first_name', 'last_name', 'email', 'phone'];

// CSS selectors for session recording redaction
const maskTextSelectors = [
	'.profile-icon',
	'.mat-column-email',
	'.mat-column-name',
	'.mat-column-phone',
	'.member-photo-with-name',
	'.name-text',
	'.name',
	'.phone',
	'.email',
];

const blockSelectors = ['.ph-no-capture'];

// Percentage of events to send to PostHog and minimum session duration, based on environment
const posthogSampleRate = 1.0;
const minSessionDurationMs = environment.production ? 10000 : 0;

type Properties = Record<string, unknown>;

// PostHog configuration object with session recording settings
export const posthogConfig = {
	person_profiles: 'identified_only' as const,
	session_recording: {
		maskAllInputs: true,
		maskInputOptions: {
			password: true,
			email: true,
		},
		maskTextSelector: maskTextSelectors.join(', '),
		blockSelector: blockSelectors.join(', '),
	},
};

// PostHog CaptureResult structure (matches posthog-js types)
interface CaptureResult {
	uuid?: string;
	event?: string;
	properties?: Properties;
	$set?: Properties;
	$set_once?: Properties;
	timestamp?: Date;
}

// Return type for the beforeSend function
interface ProcessedCaptureResult {
	uuid: string;
	event: string | undefined;
	properties: Properties;
	$set: Properties;
	$set_once: Properties;
	timestamp: Date | undefined;
}

// Match property names against the defined list
function filterProperties(value: Properties): Properties {
	return Object.entries(value).reduce<Properties>((acc, [key, val]) => {
		if (redactedProperties.some(prop => key.includes(prop))) {
			acc[key] = null;
		} else {
			acc[key] = val;
		}
		return acc;
	}, {});
}

export const posthog_before_send = (
	event: CaptureResult | null | undefined
): ProcessedCaptureResult | null => {
	if (!event) {
		return null;
	}

	const sessionStartStr = sessionStorage.getItem('ph_session_start');
	const sessionStart = sessionStartStr ? Number(sessionStartStr) : null;
	if (sessionStart && Date.now() - sessionStart < minSessionDurationMs) {
		// Block event if session is too short
		return null;
	}

	// Sampling: randomly drop events above the sample rate
	if (posthogSampleRate < 1.0 && Math.random() > posthogSampleRate) {
		return null;
	}

	const redactedProps = filterProperties(event.properties || {});
	const redactedSet = filterProperties(event.$set || {});
	const redactedSetOnce = filterProperties(event.$set_once || {});

	// Generate a uuid if not present (PostHog will provide one, but we need to match the type)
	const uuid: string =
		event.uuid ||
		(typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
		Math.random().toString(36).substring(2);

	return {
		uuid,
		event: event.event,
		properties: redactedProps,
		$set: redactedSet,
		$set_once: redactedSetOnce,
		timestamp: event.timestamp,
	};
};
